import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Newspaper, Lightbulb, Megaphone, Linkedin, Facebook, Mail, Magnet, BookOpen, Database, CalendarCheck, MessageSquareQuote } from "lucide-react";
import { MarketingHeader, MarketingFooter, MButton, BookCTA } from "@/components/marketing";
import { PlanComparison } from "@/components/PlanComparison";
import { CONTACT_HREF } from "@/data/solutions";

const TEAL = "#22D3C5";
const EASE = [0.22, 1, 0.36, 1];

const modules = [
  { icon: Lightbulb, title: "Idégenerator", desc: "Tusindvis af skræddersyede idéer - når I mangler noget at sige." },
  { icon: Linkedin, title: "LinkedIn-opslag", desc: "I bestemmer emnet - Aicom skriver færdige opslag, klar til at poste." },
  { icon: Facebook, title: "Facebook-opslag", desc: "Samme lette flow som LinkedIn - blot tilpasset tonen på Facebook." },
  { icon: MessageSquareQuote, title: "Debatindlæg", desc: "Skarpe, overbevisende debatindlæg der sætter dagsordenen - i jeres tone." },
  { icon: Mail, title: "Nyhedsbrev", desc: "Vælg hvad det skal handle om - så leverer Aicom emne, overskrift og brødtekst." },
  { icon: Newspaper, title: "Presse", desc: "Ærlig vurdering af historien - og en færdig pressemeddelelse." },
  { icon: Megaphone, title: "Kampagner", desc: "Fra mål til færdige kampagnetekster til annoncer, landingsider mm." },
  { icon: Magnet, title: "Leadgenerator", desc: "Komplette lead magnets og guides - smukt brandet og klar til brug." },
  { icon: BookOpen, title: "Brandfortælling", desc: "Jeres historie fortalt skarpt - mission, værdier og den rette vinkel." },
  { icon: Database, title: "Vidensbank", desc: "Upload løbende tekst og dokumenter - så Aicom AI lærer jer endnu bedre at kende." },
  { icon: CalendarCheck, title: "Ugens forslag", desc: "5 automatiske kommunikationsforslag hver uge - så I altid ved hvad I skal sige." },
];


export default function AicomAIPage() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [hash]);
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      <div className="grain" />
      <MarketingHeader />

      {/* Hero — editorial, asymmetric with product visualisation */}
      <section className="relative z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 pt-16 sm:pt-20 pb-16 sm:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
            <div>
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 mb-6">
                <span className="relative inline-flex h-1.5 w-1.5">
                  <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: TEAL, opacity: 0.4 }} />
                  <span className="relative h-1.5 w-1.5 rounded-full" style={{ backgroundColor: TEAL }} />
                </span>
                <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Aicom AI</span>
              </motion.div>
              <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: EASE }}
                className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-[1.05] text-balance">
                Mød din nye digitale <span style={{ color: TEAL }}>{"kommunikations\u00ADrådgiver"}</span>
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: EASE }}
                className="text-muted-foreground mt-6 text-base sm:text-lg leading-relaxed max-w-xl">
                Aicom AI lærer din virksomhed indgående at kende, foreslår hvad I bør sige - og producerer præcist det indhold, I har brug for, når I har brug for det. Med Aicom AI får I 24/7 adgang til en hurtigteksekverende, knivskarp og bundsolid digital kommunikationsrådgiver.
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24, ease: EASE }}
                className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <MButton to="/kom-i-gang" size="lg" data-testid="hero-cta-btn">Prøv Aicom AI gratis <ArrowRight className="h-4 w-4" /></MButton>
                <MButton href="#priser" size="lg" variant="secondary" data-testid="hero-secondary-btn">Se priser</MButton>
              </motion.div>
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.34 }}
                className="text-xs text-muted-foreground mt-4">Gratis at starte · Intet kort påkrævet · Klar på 2 minutter</motion.p>
            </div>

            {/* Product visualisation */}
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
              aria-hidden="true"
              className="rounded-2xl border border-border bg-card p-5 sm:p-6 select-none"
              style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.04), 0 20px 50px -30px rgba(17,17,17,0.2)" }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="relative inline-flex h-2 w-2">
                    <span className="absolute inset-0 rounded-full aic-pulse-ring" style={{ backgroundColor: TEAL, opacity: 0.4 }} />
                    <span className="relative h-2 w-2 rounded-full" style={{ backgroundColor: TEAL }} />
                  </span>
                  <span className="text-[13px] font-medium text-foreground">Aicom AI</span>
                </div>
                <span className="rounded-full border border-border px-3 py-1 text-[11px] tracking-wide text-muted-foreground">Forslag klar</span>
              </div>
              <div className="my-5 h-px bg-border" />
              <p className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground/70 mb-3">Virksomhedsprofil</p>
              <div className="grid grid-cols-2 gap-2 mb-5">
                {[["Idégenerator", Lightbulb], ["Relevant vinkel", MessageSquareQuote]].map(([label, Ic]) => (
                  <div key={label} className="flex items-center gap-2 rounded-lg border border-border bg-secondary/60 px-3 py-2">
                    <Ic className="h-3.5 w-3.5 shrink-0" style={{ color: TEAL }} strokeWidth={1.75} />
                    <span className="text-[12px] text-foreground truncate">{label}</span>
                  </div>
                ))}
              </div>
              <div className="space-y-2.5">
                {[["Indholdstype", "LinkedIn-opslag", Linkedin], ["Indholdstype", "Nyhedsbrev", Mail], ["Indholdstype", "Pressehistorie", Newspaper]].map(([tag, label, Ic]) => (
                  <div key={label} className="flex items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-2.5">
                    <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-secondary shrink-0">
                      <Ic className="h-3.5 w-3.5" style={{ color: TEAL }} strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground/70">{tag}</p>
                      <p className="text-[13px] font-medium text-foreground leading-tight truncate">{label}</p>
                    </div>
                    <span className="ml-auto h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: TEAL }} />
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Modules */}
      <section className="relative z-10 border-y border-border bg-secondary/50">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-24">
          <div className="max-w-2xl mb-12">
            <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Produktfunktioner</p>
            <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-balance">Det har aldrig været nemmere at være synlig, relevant og interessant</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-4">
            {modules.map((m, i) => {
              const span = i < 2 ? "lg:col-span-3" : "lg:col-span-2";
              return (
                <motion.div key={m.title} className={span}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
                  transition={{ duration: 0.4, delay: (i % 3) * 0.05, ease: EASE }}
                  data-testid={`landing-module-${i}`}>
                  <div className="group h-full rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-0.5 hover:border-foreground/25"
                    style={{ boxShadow: "0 1px 2px rgba(17,17,17,0.03)" }}>
                    <div className="h-10 w-10 rounded-xl bg-secondary flex items-center justify-center mb-4 transition-colors duration-300 group-hover:bg-foreground">
                      <m.icon className="h-5 w-5 text-foreground transition-colors duration-300 group-hover:text-background" strokeWidth={1.75} />
                    </div>
                    <h3 className="font-display text-lg font-medium">{m.title}</h3>
                    <p className="text-[13.5px] text-muted-foreground mt-2 leading-relaxed">{m.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="priser" className="relative z-10 max-w-6xl mx-auto px-6 py-24">
        <PlanComparison />
      </section>

      <BookCTA />
      <MarketingFooter />
    </div>
  );
}
